import Http from "../http";

// `e.response` is undefined whenever the request never reached the server —
// a timeout, a dropped connection, CORS. Reading `.data` off it threw a
// TypeError out of the catch block, so the function rejected instead of
// returning, and callers that `await` it without a try/catch died mid-handler.
// Same failure-is-undetectable family as SAM-257; guarded throughout.

const EMPTY_USER_PAGE = { docs: [], total: 0, pages: 0 };

const getUserPage = async (path, params) => {
  try {
    const response = await Http.sharedInstance.withAuth().get(path, {
      params: params,
    });
    const data = response && response.data && response.data.data;
    // SAM-290: a server error can arrive as HTTP 200 with `{ success: false }`
    // (or, post-fix, as a real 5xx landing in catch). Either way the caller
    // iterates `.docs`, so never hand it a shape without one.
    if (!data || !Array.isArray(data.docs)) {
      return { ...EMPTY_USER_PAGE };
    }
    return data;
  } catch (e) {
    return { ...EMPTY_USER_PAGE };
  }
};

// SAM-007: the full roster (phone, project/job associations, account status).
// `GET /v2/users` is project_manager and up — an inspector gets 403. Only the
// Users admin screen may call this.
export const getUsersList = async (params) => getUserPage(`/v2/users`, params);

// SAM-007: the thin picker directory — _id, ID, name, email, role, imgUrl,
// inTraining, skills, and nothing else. Readable by inspectors, so every
// assignee/autocomplete picker must use this and not getUsersList.
export const getUsersDirectory = async (params) =>
  getUserPage(`/v2/users/directory`, params);

export const getUser = async (params) => {
  const path = `/v2/users/${params.id}`;
  try {
    const response = await Http.sharedInstance.withAuth().get(path);
    return response.data;
  } catch (e) {
    return {};
  }
};

export const createUser = async (params) => {
  const path = `/v2/users`;
  try {
    const response = await Http.sharedInstance.withAuth().post(path, {
      email: params.email,
      role: params.role,
      ...(params.companies ? { companies: params.companies } : {}),
      ...(params.client_projects
        ? { client_projects: params.client_projects }
        : {}),
    });
    return response.data;
  } catch (e) {
    return {
      success: false,
      data: e.response ? e.response.data : null,
    };
  }
};

export const confirmUser = async (token) => {
  const path = `/v2/users/confirm/${token}`;
  try {
    const response = await Http.sharedInstance.getClient().put(path, {});
    return response.data;
  } catch (e) {
    // SAM-257: was `return []`. custom_signup.js checks `res.data`, which came
    // back undefined off the array, so a server outage was reported to the
    // invitee as "invalid token". Every other function in this file already
    // returns a `{ success, data }` shape.
    return { success: false, data: null };
  }
};

// `token` is the `?tokenId=` from the invitation email, and it is REQUIRED:
// /v2/users/register calls validateRegistrationToken(email, token), which
// 403s without it. This call omitted it (the TODO that used to sit here), so
// invite acceptance has been failing outright since registration was hardened
// against account takeover. Every caller must pass params.token through.
export const completeUser = async (params) => {
  const path = `/v2/users/register`;
  try {
    const response = await Http.sharedInstance.getClient().put(path, {
      email: params.email,
      password: params.password,
      name: params.name,
      phoneNumber: params.phone_number,
      terms_and_privacy: params.terms_and_privacy,
      avatar: params.avatar ? params.avatar : null,
      token: params.token,
    });
    return response.data;
  } catch (e) {
    return {
      success: false,
      data: e.response ? e.response.data : null,
    };
  }
};

export const getCurrentUser = async () => {
  const path = `/v2/users/me`;
  try {
    const response = await Http.sharedInstance.withAuth().get(path);
    return response.data;
  } catch (e) {
    return {
      success: false,
      data: {},
    };
  }
};

export const getUserActivities = async (params) => {
  const path = `/v2/users/activities?user=${params.userId}`;
  try {
    const response = await Http.sharedInstance.withAuth().get(path, {
      params: params,
    });
    return response.data;
  } catch (e) {
    return [];
  }
};

/**
 * TODO: add the new settings for the project fields and create the API for listing teams
 * @param data
 * @return {Promise<any|{success: boolean}>}
 */

export const connectWithMs = async (data) => {
  try {
    const response = await Http.sharedInstance
      .withAuth()
      .put("/v2/users/ms-connect", data);
    return response.data;
  } catch (e) {
    return {
      success: false,
    };
  }
};

// SAM-307: every user write in this frontend routes through here, and most
// callers hand it a payload straight off the wire — `/v2/users/me` for the
// profile screen, a `GET /v2/users` row for the admin editor and the
// notifications modal. Neither answers with `id`, only `_id`, so keying the
// URL on `params.id` alone addressed the write to `/v2/users/undefined`. The
// fallback belongs here rather than in each caller (`editor.js` had already
// hand-patched its own copy; its sibling notifications save had not).
export const updateUser = async (params) => {
  const path = `/v2/users/${params.id || params._id}`;
  try {
    const response = await Http.sharedInstance.getClient().put(path, params);
    return response.data;
  } catch (e) {
    return {
      success: false,
      data: e.response && e.response.data ? e.response.data : {},
    };
  }
};

export const logout = async () => {
  const path = `/v2/auth/logout`;
  try {
    const response = await Http.sharedInstance.withAuth().post(path);
    return response.data;
  } catch (e) {
    return {
      success: false,
    };
  }
};

// SAM-071: this was a bare XMLHttpRequest in my_profile/profile.js, so it
// never went through the axios auth interceptor. `POST /v2/users/subscribe/:id`
// is behind AuthGuard, so every browser that granted push permission got a 401
// here and its subscription was never stored — leaving `user.pushNotifications`
// permanently empty, which is why no push notification could ever be delivered.
export const subscribePush = async (userId, subscription) => {
  const path = `/v2/users/subscribe/${userId}`;
  try {
    const response = await Http.sharedInstance
      .withAuth()
      .post(path, subscription);
    return response.data;
  } catch (e) {
    return {
      success: false,
      data: e.response && e.response.data ? e.response.data : {},
    };
  }
};

export const reinviteUsers = async (params) => {
  const path = `/v2/users/reinvite`;
  try {
    const response = await Http.sharedInstance.getClient().post(path, {
      data: params.users,
    });
    return response.data;
  } catch (e) {
    return {
      success: false,
    };
  }
};

export const banMultiple = async (params) => {
  const path = `/v2/users/ban`;
  try {
    const response = await Http.sharedInstance.getClient().put(path, {
      data: params.users,
    });
    return response.data;
  } catch (e) {
    return {
      success: false,
    };
  }
};

export const feedsUsers = async (params) => {
  const path = `/v2/users/${params.id}/notifications`;
  try {
    const response = await Http.sharedInstance.getClient().get(path, {
      params,
    });
    return response.data;
  } catch (e) {
    return {
      success: false,
      data: {},
    };
  }
};

export const forgotPassword = async (email) => {
  const path = `/v2/users/forgot_password`;

  try {
    const response = await Http.sharedInstance.getClient().post(path, {
      email,
    });
    return response.data;
  } catch (e) {
    return {
      success: false,
      data: e.response ? e.response.data : null,
    };
  }
};

export const validateForgotPasswordToken = async (token) => {
  const path = `/v2/users/forgot_password/validate_token/${token}`;

  try {
    const response = await Http.sharedInstance.getClient().get(path);
    return response.data;
  } catch (e) {
    return {
      success: false,
      data: e.response ? e.response.data : null,
    };
  }
};

export const resetPassword = async (email, params) => {
  const path = `/v2/users/forgot_password/reset_password/${email}`;

  try {
    const response = await Http.sharedInstance.getClient().put(path, params);
    return response.data;
  } catch (e) {
    return {
      success: false,
      data: e.response ? e.response.data : null,
    };
  }
};

export const eraseUsers = async (params) => {
  const path = `/v2/users/remove`;

  try {
    const response = await Http.sharedInstance.getClient().post(path, params);
    return response.data;
  } catch (e) {
    return {
      success: false,
    };
  }
};
